import { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Form, Message, Modal } from 'semantic-ui-react';
import {
    getPrimaryLeader,
    isEligibleCandidate,
} from './teamMembership';
import {
    getAllMembers,
    getAvailableMembers,
    getMembershipErrorMessage,
} from './teamMembershipService';

const SELECT_ID = 'team-membership-person';

const getBlockedReason = (kind, membershipType, members) => {
    if (!kind.allowsSecondary || membershipType === 'secondary') return '';
    const existing = getPrimaryLeader(members, kind.role);
    return existing
        ? `${existing.name} is already this team's primary ${kind.label}. Remove them first, or add this person as secondary.`
        : '';
};

export const AddTeamMemberModal = ({ kind, members, canAddPrimary, canAddSecondary, onClose, onAdded }) => {
    const allowsSecondary = kind.allowsSecondary && canAddSecondary;
    const [membershipType, setMembershipType] = useState(() =>
        canAddPrimary && !(allowsSecondary && getPrimaryLeader(members, kind.role)) ? 'primary' : 'secondary'
    );
    const [candidates, setCandidates] = useState([]);
    const [loadingCandidates, setLoadingCandidates] = useState(true);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [fieldError, setFieldError] = useState('');
    const [formError, setFormError] = useState('');
    const isMultiSelect = kind.allowsSecondary && membershipType === 'secondary';

    useEffect(() => {
        let cancelled = false;
        const load = kind.role ? getAllMembers : getAvailableMembers;
        load()
            .then((people) => { if (!cancelled) setCandidates(people); })
            .catch((err) => { if (!cancelled) setFormError(getMembershipErrorMessage(err)); })
            .finally(() => { if (!cancelled) setLoadingCandidates(false); });
        return () => { cancelled = true; };
    }, [kind]);

    const options = useMemo(() => candidates
        .filter((person) => isEligibleCandidate(person, kind, members))
        .map((person) => ({ key: person.userId, value: person.userId, text: `${person.name} (${person.role})` })),
    [candidates, kind, members]);

    const blockedReason = getBlockedReason(kind, membershipType, members);

    const selectMembershipType = (type) => {
        setMembershipType(type);
        setSelectedUserIds([]);
        setFieldError('');
    };

    const handleSubmit = () => {
        if (blockedReason) return;
        if (membershipType === 'primary' ? !canAddPrimary : !allowsSecondary) return;
        if (selectedUserIds.length === 0) {
            setFieldError(isMultiSelect ? 'Select at least one person to add.' : 'Select a person to add.');
            return;
        }
        const people = selectedUserIds.map((userId) => candidates.find((c) => c.userId === userId));
        if (people.some((person) => !person || !isEligibleCandidate(person, kind, members))) {
            setFieldError('A selected person is no longer available to add.');
            return;
        }
        setFieldError('');
        setFormError('');
        onAdded([...members, ...people.map((person) => ({ ...person, isSecondary: membershipType === 'secondary' }))]);
    };

    return (
        <Modal open size="tiny" onClose={onClose}>
            <Modal.Header>{kind.action}</Modal.Header>
            <Modal.Content>
                {formError && <Message negative role="alert">{formError}</Message>}
                <Form>
                    {kind.allowsSecondary && (
                        <Form.Group grouped>
                            <label>Membership</label>
                            <Form.Radio
                                id="membership-type-primary"
                                label="Primary"
                                name="membershipType"
                                value="primary"
                                checked={membershipType === 'primary'}
                                disabled={!canAddPrimary}
                                onChange={() => selectMembershipType('primary')}
                            />
                            <Form.Radio
                                id="membership-type-secondary"
                                label="Secondary"
                                name="membershipType"
                                value="secondary"
                                checked={membershipType === 'secondary'}
                                disabled={!allowsSecondary}
                                onChange={() => selectMembershipType('secondary')}
                            />
                        </Form.Group>
                    )}
                    {blockedReason && <Message warning visible>{blockedReason}</Message>}
                    <Form.Field error={!!fieldError}>
                        <label htmlFor={SELECT_ID}>{kind.key === 'member' ? 'Member' : `${kind.label[0].toUpperCase()}${kind.label.slice(1)}`}</label>
                        <Dropdown
                            key={isMultiSelect ? 'multiple' : 'single'}
                            searchInput={{ id: SELECT_ID }}
                            placeholder="Search by name"
                            fluid
                            search
                            selection
                            clearable
                            multiple={isMultiSelect}
                            loading={loadingCandidates}
                            disabled={loadingCandidates}
                            options={options}
                            value={isMultiSelect ? selectedUserIds : (selectedUserIds[0] ?? null)}
                            noResultsMessage="No eligible people found."
                            onChange={(e, { value }) => {
                                setSelectedUserIds(Array.isArray(value) ? value : value ? [value] : []);
                                setFieldError('');
                            }}
                        />
                        {fieldError && <div className="ui pointing red basic label" role="alert">{fieldError}</div>}
                    </Form.Field>
                    {!loadingCandidates && options.length === 0 && !formError && (
                        <Message info>No eligible people are available to add to this team.</Message>
                    )}
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button type="button" onClick={onClose}>Cancel</Button>
                <Button
                    type="button"
                    primary
                    onClick={handleSubmit}
                    disabled={!!blockedReason || loadingCandidates}
                >
                    Add to team
                </Button>
            </Modal.Actions>
        </Modal>
    );
};
